/**
 * Event details modal (PRD §5.4c) — click-through for every calendar source.
 * Full fields per source, attribution credit, and footer actions: Track (the
 * workspace picker) and Add to Calendar. Legislative events use the existing
 * per-event .ics endpoint; authored and election events generate the .ics
 * client-side from envelope data.
 */
import React, { useState } from 'react';
import { Download, ExternalLink } from 'lucide-react';
import Button from '@shared/ui/Button';
import EventSourceBadge, { EventAttribution } from './EventSourceBadge';
import EventWorkspaceSelectionModal from '../EventWorkspaceSelectionModal';
import { to12h } from './TimeComboBox';
import type { CalendarEvent } from '../../types/calendar';

interface EventDetailsModalProps {
  event: CalendarEvent;
  isOpen: boolean;
  onClose: () => void;
}

function icsEscape(value: string): string {
  return value.replace(/\\/g, '\\\\').replace(/;/g, '\\;').replace(/,/g, '\\,').replace(/\n/g, '\\n');
}

/** Build a VCALENDAR for an authored/election event from envelope data. */
export function buildEventICS(event: CalendarEvent): string {
  const date = String(event.event_date || event.reading_date || '').slice(0, 10).replace(/-/g, '');
  const start = event.start_time ? String(event.start_time).slice(0, 5).replace(':', '') + '00' : null;
  const end = event.end_time ? String(event.end_time).slice(0, 5).replace(':', '') + '00' : null;
  const location = [event.venue, event.address, event.city].filter(Boolean).join(', ');
  const lines = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//Capitol Commons//Calendar//EN',
    'BEGIN:VEVENT',
    `UID:${event.event_key || `cc-${date}`}@capitolcommons.ai`,
    start ? `DTSTART;TZID=America/Denver:${date}T${start}` : `DTSTART;VALUE=DATE:${date}`,
    ...(start && end ? [`DTEND;TZID=America/Denver:${date}T${end}`] : []),
    `SUMMARY:${icsEscape(event.title || event.reading_title || 'Event')}`,
    ...(event.calendar_description ? [`DESCRIPTION:${icsEscape(event.calendar_description)}`] : []),
    ...(location ? [`LOCATION:${icsEscape(location)}`] : []),
    'END:VEVENT',
    'END:VCALENDAR',
  ];
  return lines.join('\r\n');
}

function downloadICS(event: CalendarEvent) {
  const blob = new Blob([buildEventICS(event)], { type: 'text/calendar;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `${(event.title || event.reading_title || 'event').replace(/[^\w-]+/g, '_')}.ics`;
  link.click();
  URL.revokeObjectURL(url);
}

function googleCalendarUrl(event: CalendarEvent): string {
  const date = String(event.event_date || event.reading_date || '').slice(0, 10).replace(/-/g, '');
  const start = event.start_time ? String(event.start_time).slice(0, 5).replace(':', '') + '00' : null;
  const end = event.end_time ? String(event.end_time).slice(0, 5).replace(':', '') + '00'
    : start ? String(parseInt(start.slice(0, 2), 10) + 1).padStart(2, '0') + start.slice(2) : null;
  const dates = start && end ? `${date}T${start}/${date}T${end}` : `${date}/${date}`;
  const params = new URLSearchParams({
    action: 'TEMPLATE',
    text: event.title || event.reading_title || 'Event',
    dates,
    ctz: 'America/Denver',
    details: event.calendar_description || '',
    location: [event.venue, event.address, event.city].filter(Boolean).join(', '),
  });
  return `https://calendar.google.com/calendar/render?${params.toString()}`;
}

const detailRow = (label: string, value: React.ReactNode) =>
  value ? (
    <div className="grid grid-cols-[110px_1fr] gap-2 text-sm">
      <dt className="text-text-tertiary dark:text-text-dark-tertiary">{label}</dt>
      <dd className="text-text-primary dark:text-text-dark-primary break-words">{value}</dd>
    </div>
  ) : null;

const EventDetailsModal: React.FC<EventDetailsModalProps> = ({ event, isOpen, onClose }) => {
  const [trackOpen, setTrackOpen] = useState(false);
  if (!isOpen) return null;

  const source = event.event_source ?? 'legislative';
  const trackable = (source === 'public' || source === 'legislative') && event.event_key;
  const isLegislative = source === 'legislative';
  const date = String(event.event_date || event.reading_date || '').slice(0, 10);
  const time = event.all_day || event.no_time
    ? 'All day'
    : [event.start_time || event.reading_time, event.end_time]
      .filter(Boolean)
      .map((t) => to12h(String(t).slice(0, 5)))
      .join(' – ');
  const location = [event.venue, event.address, event.city, event.county]
    .filter(Boolean).join(', ') || event.derived_location || event.room_number;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" role="dialog" aria-modal="true">
      <div className="w-full max-w-md rounded-lg bg-white dark:bg-background-dark-secondary shadow-xl flex flex-col max-h-[85vh]">
        <div className="border-b border-border-light dark:border-border-dark-hairline px-5 py-3.5 flex items-start justify-between gap-3">
          <div>
            <h2 className="font-display font-semibold text-[15px] text-primary-800 dark:text-text-dark-primary">
              {event.title || event.reading_title}
            </h2>
            <div className="mt-1 flex items-center gap-2">
              <EventSourceBadge source={source} workspaceName={event.workspace_name} />
              <EventAttribution attribution={event.attribution ?? null} />
            </div>
          </div>
        </div>

        <dl className="flex-1 overflow-y-auto px-5 py-4 space-y-2">
          {detailRow('Date', date)}
          {detailRow('Time', time)}
          {detailRow('Location', location)}
          {detailRow('Format', event.format && event.format !== 'unknown' ? event.format.replace('_', ' ') : null)}
          {detailRow('Host', (event as any).host_name
            ? `${(event as any).host_name}${(event as any).host_party ? ` (${(event as any).host_party})` : ''}`
            : null)}
          {detailRow('Topics', Array.isArray((event as any).topics) && (event as any).topics.length > 0
            ? ((event as any).topics as string[]).join(', ') : null)}
          {detailRow('Chamber', isLegislative ? event.chamber : null)}
          {detailRow('About', event.calendar_description)}
          {detailRow('Event link', (event as any).link_url ? (
            <a href={(event as any).link_url} target="_blank" rel="noopener noreferrer"
              className="text-primary-600 dark:text-primary-dark-500 hover:underline inline-flex items-center gap-1">
              Open <ExternalLink className="w-3 h-3" aria-hidden="true" />
            </a>
          ) : null)}
          {detailRow('RSVP', (event as any).registration_url ? (
            <a href={(event as any).registration_url} target="_blank" rel="noopener noreferrer"
              className="text-primary-600 dark:text-primary-dark-500 hover:underline inline-flex items-center gap-1">
              Register <ExternalLink className="w-3 h-3" aria-hidden="true" />
            </a>
          ) : null)}
        </dl>

        <div className="flex items-center justify-between border-t border-border-light dark:border-border-dark-hairline px-5 py-3">
          <div className="flex items-center gap-2">
            {trackable && (
              <Button variant="secondary" size="sm" onClick={() => setTrackOpen(true)}>
                {(event as any).is_tracked || (event as any).is_tracked_event ? 'Tracked ✓' : 'Track'}
              </Button>
            )}
            <Button
              variant="secondary"
              size="sm"
              icon={Download}
              onClick={() => {
                if (isLegislative && event.calendar_id) {
                  window.open(`/api/calendar/events/${event.calendar_id}/export.ics`, '_blank');
                } else {
                  downloadICS(event);
                }
              }}
            >
              Add to Calendar
            </Button>
            {!event.all_day && (
              <a
                href={googleCalendarUrl(event)}
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs font-semibold text-primary-600 dark:text-primary-dark-500 hover:underline"
              >
                Google
              </a>
            )}
          </div>
          <Button variant="primary" size="sm" onClick={onClose}>Close</Button>
        </div>

        {trackOpen && event.event_key && (
          <EventWorkspaceSelectionModal
            eventKey={event.event_key}
            eventTitle={event.title || event.reading_title || ''}
            isOpen
            onClose={() => setTrackOpen(false)}
          />
        )}
      </div>
    </div>
  );
};

export default EventDetailsModal;
